/**
 * Recordings API
 *
 * Provides functions to interact with the /recordings endpoint for the
 * multi-host recording system.
 *
 * Based on RECORDING_SYSTEM_IMPLEMENTATION_PLAN.md Section 1.2
 */

import { fetchWrapperPortal } from './fetch';
import type {
  Recording,
  CreateRecordingRequest,
  UpdateCompositionRequest,
  ChunkUploadUrlRequest,
  ChunkUploadUrlResponse,
  ChunkCompleteRequest,
  OpentokTokenResponse,
  RecordingPollResponse,
  ExportRecordingResponse,
  Hook,
} from '../../types/recording';

/**
 * Create a new recording session
 * POST /v2/recordings
 */
export const createRecording = async (data: CreateRecordingRequest): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: 'v2/recordings',
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Get recording by ID
 * GET /v2/recordings/:id
 */
export const getRecordingById = async (id: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}`,
    method: 'GET',
    addBearer: true,
  });

/**
 * List user's recordings
 * GET /v2/recordings/user/:userId
 */
export const getRecordingsByUser = async (userId: string): Promise<Recording[]> =>
  fetchWrapperPortal<Recording[]>({
    url: `v2/recordings/user/${userId}`,
    method: 'GET',
    addBearer: true,
  });

/**
 * Every creator's recordings in the tenant — the Library's "All videos"
 * mode. Staff-only server-side (403 otherwise); the UI gates on the same
 * predicate. Paginated server-side (limit caps at 200).
 * GET /v2/recordings?scope=all
 */
export const listAllRecordings = async (limit = 100): Promise<Recording[]> =>
  fetchWrapperPortal<Recording[]>({
    url: `v2/recordings?scope=all&limit=${limit}`,
    method: 'GET',
    addBearer: true,
  });

/**
 * Update recording metadata
 * PUT /v2/recordings/:id
 */
export const updateRecording = async (id: string, updates: Partial<Recording>): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}`,
    method: 'PUT',
    body: JSON.stringify(updates),
    addBearer: true,
  });

/**
 * Delete recording and all chunks
 * DELETE /v2/recordings/:id
 */
export const deleteRecording = async (id: string): Promise<{ success: true }> =>
  fetchWrapperPortal<{ success: true }>({
    url: `v2/recordings/${id}`,
    method: 'DELETE',
    addBearer: true,
  });

/**
 * Mark recording as started (server-side state)
 * POST /v2/recordings/:id/start
 */
export const startRecording = async (id: string, started_at: Date): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/start`,
    method: 'POST',
    body: JSON.stringify({ started_at }),
    addBearer: true,
  });

/**
 * Mark recording as stopped
 * POST /v2/recordings/:id/stop
 */
export const stopRecording = async (id: string, stopped_at: Date, duration_ms: number): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/stop`,
    method: 'POST',
    body: JSON.stringify({ stopped_at, duration_ms }),
    addBearer: true,
  });

/**
 * Mark recording as paused
 * POST /v2/recordings/:id/pause
 */
export const pauseRecording = async (id: string, timestamp_ms: number): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/pause`,
    method: 'POST',
    body: JSON.stringify({ timestamp_ms }),
    addBearer: true,
  });

/**
 * Resume recording
 * POST /v2/recordings/:id/resume
 */
export const resumeRecording = async (id: string, timestamp_ms: number): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/resume`,
    method: 'POST',
    body: JSON.stringify({ timestamp_ms }),
    addBearer: true,
  });

/**
 * Update composition (adds to timeline if recording)
 * PUT /v2/recordings/:id/composition
 */
export const updateComposition = async (id: string, data: UpdateCompositionRequest): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/composition`,
    method: 'PUT',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Add hook/question
 * POST /v2/recordings/:id/hooks
 */
export const addHook = async (id: string, hook: Omit<Hook, '_id'>): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/hooks`,
    method: 'POST',
    body: JSON.stringify(hook),
    addBearer: true,
  });

/**
 * Update hook
 * PUT /v2/recordings/:id/hooks/:hookId
 */
export const updateHook = async (id: string, hookId: string, updates: Partial<Hook>): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/hooks/${hookId}`,
    method: 'PUT',
    body: JSON.stringify(updates),
    addBearer: true,
  });

/**
 * Delete hook
 * DELETE /v2/recordings/:id/hooks/:hookId
 */
export const deleteHook = async (id: string, hookId: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/hooks/${hookId}`,
    method: 'DELETE',
    addBearer: true,
  });

/**
 * Set selected hook for recording
 * PUT /v2/recordings/:id/selected-hook
 */
export const setSelectedHook = async (id: string, selectedHook: string | null): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/selected-hook`,
    method: 'PUT',
    body: JSON.stringify({ selected_hook: selectedHook }),
    addBearer: true,
  });

/**
 * Clear selected hook for recording
 * DELETE /v2/recordings/:id/selected-hook
 */
export const clearSelectedHook = async (id: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/selected-hook`,
    method: 'DELETE',
    addBearer: true,
  });

/**
 * Get presigned S3 upload URL for chunk
 * POST /v2/recordings/:id/chunks/upload-url
 */
export const getChunkUploadUrl = async (id: string, data: ChunkUploadUrlRequest): Promise<ChunkUploadUrlResponse> =>
  fetchWrapperPortal<ChunkUploadUrlResponse>({
    url: `v2/recordings/${id}/chunks/upload-url`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Confirm chunk upload complete
 * POST /v2/recordings/:id/chunks/complete
 */
export const completeChunkUpload = async (id: string, data: ChunkCompleteRequest): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/chunks/complete`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Get Daily.co meeting token
 * GET /v2/recordings/:id/daily-token
 */
export const getDailyToken = async (id: string): Promise<OpentokTokenResponse> =>
  fetchWrapperPortal<OpentokTokenResponse>({
    url: `v2/recordings/${id}/daily-token`,
    method: 'GET',
    addBearer: true,
  });

/**
 * @deprecated Use getDailyToken instead
 */
export const getOpentokToken = getDailyToken;

/**
 * Poll recording data for delta updates
 * GET /v2/recordings/:id/poll-data?from={timestamp}&to={timestamp}
 */
export const pollRecordingData = async (id: string, from: number, to: number): Promise<RecordingPollResponse> =>
  fetchWrapperPortal<RecordingPollResponse>({
    url: `v2/recordings/${id}/poll-data?from=${from}&to=${to}`,
    method: 'GET',
    addBearer: true,
  });

/**
 * Export to editor-compatible JSON
 * POST /v2/recordings/:id/export
 */
export const exportRecording = async (id: string): Promise<ExportRecordingResponse> =>
  fetchWrapperPortal<ExportRecordingResponse>({
    url: `v2/recordings/${id}/export`,
    method: 'POST',
    addBearer: true,
  });

/**
 * Sync Daily.co recording data and fetch raw tracks
 * GET /v2/recordings/:id/sync-daily-data
 */
export const syncDailyRecordingData = async (
  id: string,
  options?: { maxRetries?: number; retryDelay?: number }
): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/sync-daily-data${options ? `?maxRetries=${options.maxRetries || 5}&retryDelay=${options.retryDelay || 5000}` : ''}`,
    method: 'GET',
    addBearer: true,
  });

/**
 * Generate scene JSON from recording data and raw tracks
 * POST /v2/recordings/:id/generate-scene
 */
export const generateSceneJson = async (id: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/generate-scene`,
    method: 'POST',
    addBearer: true,
  });

/**
 * Add participant to recording
 * POST /v2/recordings/:id/participants
 */
export const addParticipant = async (id: string, data: { email: string; message?: string }): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/participants`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Remove participant from recording
 * DELETE /v2/recordings/:id/participants/:userId
 */
export const removeParticipant = async (id: string, userId: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/participants/${userId}`,
    method: 'DELETE',
    addBearer: true,
  });

/**
 * Add cohost to recording
 * POST /v2/recordings/:id/cohosts
 */
export const addCohost = async (
  id: string,
  data: {
    email?: string;
    user_id?: string;
    invitation_email?: {
      subject?: string;
      body: string;
    };
  }
): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/cohosts`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Remove cohost from recording
 * DELETE /v2/recordings/:id/cohosts/:cohostId
 */
export const removeCohost = async (id: string, cohostId: string): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/cohosts/${cohostId}`,
    method: 'DELETE',
    addBearer: true,
  });

/**
 * Get presigned S3 upload URL for composed chunk
 * POST /v2/recordings/:id/composed-chunks/upload-url
 */
export const getComposedChunkUploadUrl = async (
  id: string,
  data: { chunk_index: number }
): Promise<ChunkUploadUrlResponse> =>
  fetchWrapperPortal<ChunkUploadUrlResponse>({
    url: `v2/recordings/${id}/composed-chunks/upload-url`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * Confirm composed chunk upload complete
 * POST /v2/recordings/:id/composed-chunks/complete
 */
export const completeComposedChunkUpload = async (
  id: string,
  data: {
    chunk_id: string;
    s3_url: string;
    start_timestamp: number;
    end_timestamp: number;
    duration_ms: number;
    size_bytes: number;
  }
): Promise<Recording> =>
  fetchWrapperPortal<Recording>({
    url: `v2/recordings/${id}/composed-chunks/complete`,
    method: 'POST',
    body: JSON.stringify(data),
    addBearer: true,
  });

/**
 * ========================================
 * MULTIPART UPLOAD METHODS
 * ========================================
 * For continuous streaming upload of composed recording
 */

/**
 * Initiate multipart upload for composed recording stream
 * POST /v2/recordings/:id/multipart/initiate
 */
export const initiateMultipartUpload = async (
  id: string,
  contentType?: string
): Promise<{ uploadId: string; key: string }> =>
  fetchWrapperPortal<{ uploadId: string; key: string }>({
    url: `v2/recordings/${id}/multipart/initiate`,
    method: 'POST',
    body: JSON.stringify({ content_type: contentType }),
    addBearer: true,
  });

/**
 * Get presigned URL for uploading a specific part
 * POST /v2/recordings/:id/multipart/part-url
 */
export const getMultipartPartUploadUrl = async (
  id: string,
  partNumber: number
): Promise<{ presignedUrl: string; partNumber: number }> =>
  fetchWrapperPortal<{ presignedUrl: string; partNumber: number }>({
    url: `v2/recordings/${id}/multipart/part-url`,
    method: 'POST',
    body: JSON.stringify({ part_number: partNumber }),
    addBearer: true,
  });

/**
 * Confirm a part has been uploaded
 * POST /v2/recordings/:id/multipart/confirm-part
 */
export const confirmMultipartPart = async (
  id: string,
  partNumber: number,
  etag: string
): Promise<{ confirmed: boolean; totalParts: number }> =>
  fetchWrapperPortal<{ confirmed: boolean; totalParts: number }>({
    url: `v2/recordings/${id}/multipart/confirm-part`,
    method: 'POST',
    body: JSON.stringify({ part_number: partNumber, etag }),
    addBearer: true,
  });

/**
 * Complete multipart upload
 * POST /v2/recordings/:id/multipart/complete
 *
 * Returns the processed video URL (with fixed duration metadata) in composed_video_url
 */
export const completeMultipartUpload = async (
  id: string
): Promise<{ location: string; key: string; composed_video_url: string }> =>
  fetchWrapperPortal<{ location: string; key: string; composed_video_url: string }>({
    url: `v2/recordings/${id}/multipart/complete`,
    method: 'POST',
    addBearer: true,
  });

/**
 * Abort multipart upload
 * POST /v2/recordings/:id/multipart/abort
 */
export const abortMultipartUpload = async (id: string): Promise<{ aborted: boolean }> =>
  fetchWrapperPortal<{ aborted: boolean }>({
    url: `v2/recordings/${id}/multipart/abort`,
    method: 'POST',
    addBearer: true,
  });

/**
 * Recovery response type for stale recording sessions
 */
export interface RecoverRecordingResponse {
  can_salvage: boolean;
  parts_count: number;
  salvaged?: boolean;
  reset?: boolean;
  composed_video_url?: string;
  recording: any; // Recording type
}

/**
 * Recover a stale recording session
 * POST /v2/recordings/:id/recover
 *
 * Used when a user returns to a recording that was interrupted (browser closed during recording).
 * Can check salvageability, attempt salvage, or reset to clean state.
 *
 * 'restart' is the live "Start Over" path: same reset, but the backend also
 * stops the Daily.co recording that is still running. The recording never
 * passes through 'stopped', so the page stays in recording mode and keeps its
 * Daily.co session instead of flipping to playback and needing a reload.
 *
 * @param id - Recording ID
 * @param action - 'check' (default), 'salvage', 'reset', or 'restart'
 */
export const recoverRecording = async (
  id: string,
  action: 'check' | 'salvage' | 'reset' | 'restart' = 'check'
): Promise<RecoverRecordingResponse> =>
  fetchWrapperPortal<RecoverRecordingResponse>({
    url: `v2/recordings/${id}/recover`,
    method: 'POST',
    addBearer: true,
    body: JSON.stringify({ action }),
  });

/**
 * Convert a raw track to MP4 (on-demand)
 * POST /v2/recordings/:id/convert-track-mp4
 *
 * Uses track_index (position in daily_recording_data.raw_tracks array) to identify the track.
 * If already converted, returns cached MP4 URL immediately.
 * If already in progress, returns current status.
 * Otherwise starts background conversion - poll GET /:id to check status.
 */
export const convertTrackToMp4 = async (
  id: string,
  trackIndex: number
): Promise<{ status: string; mp4_url?: string }> =>
  fetchWrapperPortal<{ status: string; mp4_url?: string }>({
    url: `v2/recordings/${id}/convert-track-mp4`,
    method: 'POST',
    body: JSON.stringify({ track_index: trackIndex }),
    addBearer: true,
  });

// Backwards compatibility aliases
export const getRecording = getRecordingById;
export const listUserRecordings = getRecordingsByUser;

// Export as object for easier imports
export const recordingsAPI = {
  createRecording,
  getRecording,
  getRecordingById,
  getRecordingsByUser,
  listAllRecordings,
  listUserRecordings,
  updateRecording,
  deleteRecording,
  startRecording,
  stopRecording,
  pauseRecording,
  resumeRecording,
  updateComposition,
  addHook,
  updateHook,
  deleteHook,
  getChunkUploadUrl,
  completeChunkUpload,
  getComposedChunkUploadUrl,
  completeComposedChunkUpload,
  getDailyToken,
  getOpentokToken,
  pollRecordingData,
  exportRecording,
  syncDailyRecordingData,
  generateSceneJson,
  addParticipant,
  removeParticipant,
  addCohost,
  removeCohost,
  // Multipart upload methods
  initiateMultipartUpload,
  getMultipartPartUploadUrl,
  confirmMultipartPart,
  completeMultipartUpload,
  abortMultipartUpload,
  // Recovery
  recoverRecording,
  // Track MP4 conversion
  convertTrackToMp4,
};
